import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import copyToClipboard from 'util/clipboard/copyToClipboard';

export interface PublicKeySectionProps {
  value: string | null;
  onChangePublicKey: (newPublicKey: string) => void;
}

interface ClipboardButtonProps {
  getValue: () => string;
}

export function ClipboardButton(props: ClipboardButtonProps): JSX.Element | null {
  return (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy public key">
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        size={ButtonSize.MINI}
        className={css(styles.upperRight)}
        icon="copy"
        onClick={() =>
          copyToClipboard(props.getValue(), {
            showToastOnSuccess: true,
            toastText: 'Copied public key to clipboard.',
          })
        }
      />
    </Tooltip>
  );
}

export default function PublicKeySection(props: PublicKeySectionProps): JSX.Element | null {
  function getValue(): string {
    return props.value ?? '';
  }

  return (
    <InputWrapper
      id={_.uniqueId('benchlingAppPublicKey')}
      label="Public key"
      labelStyle={FieldLabel.STYLES.LABEL}
      help="This key is used to encrypt values stored securely in app configs."
      helpInteraction={FloatingInteraction.HOVER}
    >
      <div className={css(styles.publicKeyContainer)}>
        <EditableTextField
          inputClassName={css(styles.editableTextFieldClass)}
          emptyValueAllowed={true}
          placeholder="Public key for encrypting secure_text app config items"
          value={props.value ?? ''}
          requestChange={props.onChangePublicKey}
          type={TextFieldType.TEXTAREA}
          breakLines={true}
          dataTestAttrs={{component: 'PublicKeySection', element: 'editableTextField'}}
        />
        {props.value != null && <ClipboardButton getValue={getValue} />}
      </div>
    </InputWrapper>
  );
}

const LARGE_TEXTAREA_HEIGHT = 180;
const styles = StyleSheet.create({
  publicKeyContainer: {
    position: 'relative',
  },
  upperRight: {
    position: 'absolute',
    right: 0,
    top: 0,
  },
  editableTextFieldClass: {
    minHeight: LARGE_TEXTAREA_HEIGHT,
    width: '100%',
  },
});
